<script>
  import { onMount } from 'svelte';
  import {
    COLORS, PADDING, buildScales, clearCanvas, drawGrid, drawTimeLabels,
    drawCrosshair, indexFromX, summarize,
  } from './chartUtils.js';
  import { deriveReturns, formatSignedPercent } from '../../../lib/strategyResult.js';

  // Takes the equity curve and derives per-period returns client-side (KTD4).
  export let data = []; // [{ time, value, cash }]

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hoverIndex = -1;

  const fmt = (v) => formatSignedPercent(v);
  $: series = deriveReturns(data);
  $: hasData = series.length >= 2;
  $: summary = summarize(series, 'Per-period returns', fmt);

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 200;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    // Symmetric range around zero so the baseline is centered.
    let mag = 0;
    for (const p of series) mag = Math.max(mag, Math.abs(p.value));
    if (mag === 0) mag = 0.01;
    const scales = buildScales(series, width, height, { valueMin: -mag, valueMax: mag });
    drawGrid(ctx, width, height, scales, fmt);
    drawTimeLabels(ctx, series, height, scales);

    const zeroY = scales.yOf(0);
    const plotW = width - PADDING.left - PADDING.right;
    const barW = Math.max(1, (plotW / series.length) * 0.7);
    series.forEach((p, i) => {
      const x = scales.xOf(i);
      const y = scales.yOf(p.value);
      ctx.fillStyle = p.value >= 0 ? COLORS.gain : COLORS.loss;
      const top = Math.min(y, zeroY);
      const h = Math.max(1, Math.abs(y - zeroY));
      ctx.fillRect(x - barW / 2, top, barW, h);
    });

    // Zero baseline.
    ctx.strokeStyle = COLORS.label;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(PADDING.left, zeroY);
    ctx.lineTo(width - PADDING.right, zeroY);
    ctx.stroke();
    ctx.globalAlpha = 1;

    if (hoverIndex >= 0) drawCrosshair(ctx, series, hoverIndex, width, height, scales, fmt);
  }

  // Depend on `series` directly, not just `hasData`: switching between two non-empty runs leaves hasData
  // === true, so a hasData-only guard never re-runs and the canvas keeps the prior run's drawing.
  $: if (ctx && canvas && hasData && width && height && series && (hoverIndex || hoverIndex === -1)) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverIndex = indexFromX(e.clientX - rect.left, series.length, width);
  }
  function onLeave() { hoverIndex = -1; }

  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(series.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      const p = series[hoverIndex];
      liveMsg = `${new Date(p.time).toLocaleDateString()}: ${fmt(p.value)}`;
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Per-Period Returns</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Time</th><th>Return</th></tr></thead>
        <tbody>
          {#each series as p}<tr><td>{new Date(p.time).toLocaleDateString()}</td><td>{fmt(p.value)}</td></tr>{/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
